import { useEffect, useRef, useState } from "react";
import type { WorldSnapshot } from "@frontier/protocol/contracts";
import { browserAuthStorage } from "./auth-storage";
import { AUDIO_SETTINGS_KEY, readAudioSettings } from "./audio/audio-settings";
import type { AudioSettings } from "./audio/audio-settings";
import { GameAudio } from "./audio/game-audio";
import type { AudioStatus } from "./audio/game-audio";
import { SnapshotAudio } from "./audio/snapshot-audio";

export interface AudioControl {
  settings: AudioSettings;
  status: AudioStatus;
  change: (settings: AudioSettings) => void;
}
interface Props {
  world: WorldSnapshot | null;
  worldReceivedAt: number;
  playerId: string;
  runId: string;
  connected: boolean;
  targetId: string;
}

export function useGameAudio(props: Props): AudioControl {
  const [storage] = useState(browserAuthStorage);
  const [settings, setSettings] = useState(() => readAudioSettings(storage));
  const [status, setStatus] = useState<AudioStatus>("waiting");
  const audio = useRef<GameAudio | null>(null);
  const tracker = useRef(new SnapshotAudio());
  const latestSettings = useRef(settings);
  latestSettings.current = settings;
  useEffect(() => {
    const controller = new GameAudio(latestSettings.current, setStatus);
    audio.current = controller;
    const gesture = () => { void controller.unlock(); };
    const activity = () => {
      tracker.current.reset();
      controller.setActive(document.visibilityState !== "hidden" && document.hasFocus());
    };
    const blur = () => {
      tracker.current.reset();
      controller.setActive(false);
    };
    const click = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest('button,summary,[role="button"]');
      if (!button || button.closest("[data-audio-settings]") || button.matches(":disabled,[aria-disabled=true]")) return;
      controller.play({ id: "click", gain: 0.45, pan: 0 });
    };
    window.addEventListener("pointerdown", gesture, true);
    window.addEventListener("keydown", gesture, true);
    document.addEventListener("click", click, true);
    document.addEventListener("visibilitychange", activity);
    window.addEventListener("blur", blur);
    window.addEventListener("focus", activity);
    activity();
    return () => {
      window.removeEventListener("pointerdown", gesture, true);
      window.removeEventListener("keydown", gesture, true);
      document.removeEventListener("click", click, true);
      document.removeEventListener("visibilitychange", activity);
      window.removeEventListener("blur", blur);
      window.removeEventListener("focus", activity);
      tracker.current.reset();
      controller.dispose();
      audio.current = null;
    };
  }, []);
  useEffect(() => {
    audio.current?.configure(settings);
    storage.setItem(AUDIO_SETTINGS_KEY, JSON.stringify(settings));
  }, [settings, storage]);
  useEffect(() => {
    if (!props.connected || !props.world || !props.playerId) {
      tracker.current.reset();
      audio.current?.receive({ cues: [], music: "station", engine: 0 });
      return;
    }
    audio.current?.receive(tracker.current.receive(
      props.world, props.playerId, props.runId, props.worldReceivedAt,
    ));
  }, [props.world, props.worldReceivedAt, props.playerId, props.runId, props.connected]);
  const previousTarget = useRef(props.targetId);
  useEffect(() => {
    if (props.targetId && previousTarget.current !== props.targetId)
      audio.current?.play({ id: "select", gain: 0.5, pan: 0 });
    previousTarget.current = props.targetId;
  }, [props.targetId]);
  return { settings, status, change: setSettings };
}
